<script setup>
import { ref, nextTick, onMounted, computed, onBeforeUnmount } from "vue";
import { useNoteTypeStore } from "@/stores/useNoteTypeStore";
import { toTitleCase } from "@/utils/stringUtils";

const props = defineProps({
  initialName: { type: String, default: "" },
});

const emit = defineEmits(["close", "note-type-added"]);

const noteTypeStore = useNoteTypeStore();

const obj = ref({
  noteTypeId: 0,
  noteTypeName: "",
  defaultRequiredBackground: false,
  defaultColor: "#000000",
  defaultBackground: "transparent",
  defaultRequiredUnderline: true,
  defaultUnderline: "#000000",
});

// Constante para o estilo de fundo quadriculado transparente
const transparentBackgroundStyle = computed(() => {
  const isDark = document.documentElement.classList.contains("dark");
  const color = isDark ? "#555" : "#ccc";

  return {
    backgroundImage: `
      linear-gradient(45deg, ${color} 12.5%, transparent 12.5%, transparent 87.5%, ${color} 87.5%),
      linear-gradient(45deg, ${color} 12.5%, transparent 12.5%, transparent 87.5%, ${color} 87.5%)
    `,
    backgroundSize: "20px 20px",
    backgroundPosition: "0 0, 10px 10px",
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
    opacity: "0.85",
  };
});

// Prévia para PALAVRAS ÚNICAS (chip)
const singleWordPreviewStyle = computed(() => {
  const isTransparent =
    obj.value.defaultBackground === "transparent" ||
    !obj.value.defaultRequiredBackground;

  let style = {};

  if (isTransparent) {
    // Usa o estilo quadriculado
    style = {
      ...transparentBackgroundStyle.value,
      color: obj.value.defaultColor,
    };
  } else {
    // Usa a cor de fundo sólida
    style = {
      backgroundColor: obj.value.defaultBackground,
      color: obj.value.defaultColor,
    };
  }

  style.borderRadius = "999px";
  style.padding = "0.4rem 0.75rem";
  style.display = "inline-flex";
  style.alignItems = "center";
  style.justifyContent = "center";
  style.fontWeight = "600";
  style.border = "1px solid var(--border-color)";

  return style;
});

// Prévia para TRECHOS COM 2+ PALAVRAS (sublinhado)
const multiWordPreviewStyle = computed(() => {
  const underlineColor = obj.value.defaultUnderline || obj.value.defaultColor || "#000000";

  return {
    display: "inline-block",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 0",
    fontWeight: "500",
    color: "var(--text-dark)",
    backgroundColor: "transparent",
    borderBottom: `3px solid ${underlineColor}`,
    paddingBottom: "1px",
    lineHeight: "1.2",
  };
});

const nameInput = ref(null);

// estado de envio
const isSubmitting = ref(false);

// ====================================================================
// ✅ TOAST PADRÃO ADDUSERGROUPFORM.VUE (3000ms) 🚀
// (Copiado de AddActivityForm.vue para consistência)
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null; // Limpa a referência
  }, 3000); // 3 segundos, padrão AddUserGroupForm
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(); // Inicia o timer de 3s para o toast

  // Fecha o modal depois de 2.4s (padrão AddActivityForm.vue)
  setTimeout(() => {
    if (typeof onFinish === "function") {
      onFinish();
    }
  }, 2400); // Alterado de 1500ms local para 2400ms para consistência.
}
// ====================================================================

// TOGGLES
function handleBackgroundToggle() {
  if (!obj.value.defaultRequiredBackground) {
    obj.value.defaultBackground = "transparent";
  } else if (obj.value.defaultBackground === "transparent") {
    obj.value.defaultBackground = "#FFFFFF";
  }
}

async function cadastrar() {
  if (isSubmitting.value) return;

  try {
    isSubmitting.value = true;
    
    if (!obj.value.noteTypeName || !obj.value.noteTypeName.trim()) {
      showErrorToast("O nome da categoria de nota é obrigatório.");
      isSubmitting.value = false;
      return;
    }

    if (!obj.value.defaultUnderline || obj.value.defaultUnderline === "initial") {
      obj.value.defaultUnderline = "#000000";
    }

    const created = await noteTypeStore.createNoteType(obj.value);

    showSuccessToast("Categoria de nota criada com sucesso.", () => {
      emit("note-type-added", {
        noteTypeId: created.noteTypeId,
        noteTypeName: created.noteTypeName,
      });
      emit("close");
      isSubmitting.value = false; // Garantia de resetar isSubmitting após fechar
    });
  } catch (error) {
    let userFriendlyMessage = "Ocorreu um erro desconhecido ao cadastrar.";
    const errorData = error?.response?.data || error;

    if (errorData && errorData.errors) {
      const messages = Object.values(errorData.errors).flat();
      if (messages.length > 0) {
        userFriendlyMessage = messages.join("\n");
      }
    } else if (errorData && errorData.message) {
      userFriendlyMessage = errorData.message;
    } else if (error.message && typeof error.message === "string") {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", errorData);
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  // Adicionado isSubmitting check
  if (!isSubmitting.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(() => {
  obj.value.noteTypeName = props.initialName;
  nextTick(() => {
    if (nameInput.value) {
      nameInput.value.focus();
      nameInput.value.select();
    }
  });

  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div class="modal-backdrop" @click.self="!isSubmitting">
    <div class="modal-panel">
      <div class="modal-header">
        <h3 class="modal-title">Adicionar Categoria de Nota</h3>
        <button 
          @click="closeModal" 
          type="button" 
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="cadastrar" novalidate>
        <div class="modal-body">
          <div class="form-content">
            <div class="form-group">
              <label for="name" class="form-label">Nome</label>
              <input
                ref="nameInput"
                type="text"
                id="name"
                v-model="obj.noteTypeName"
                @blur="obj.noteTypeName = toTitleCase(obj.noteTypeName)"
                class="input-base"
                placeholder="Ex: Definição, Citação, Insight"
                required
                @focus="$event.target.select()"
              />
            </div>

            <p class="explanation-text">
              Cria a categoria e defina o estilo como ela aparecerá no texto: como <strong>palavra única</strong> ou
              como <strong>trecho com várias palavras</strong>.
            </p>

            <section class="style-section">
              <h4 class="section-title">Palavras únicas (chip)</h4>
              <p class="section-help">
                Para quando a nota marcar só uma palavra.
              </p>
              <div class="color-section">
                <div class="color-preview" :style="singleWordPreviewStyle">
                  Palavra única
                </div>
                <div class="color-pickers">
                  <div class="color-picker-group checkbox-and-picker">
                    <label class="toggle-control-group" for="default-required-background-toggle">
                      <span class="form-label toggle-label-text">Cor de fundo</span>
                      
                      <input
                        id="default-required-background-toggle"
                        type="checkbox"
                        v-model="obj.defaultRequiredBackground"
                        @change="handleBackgroundToggle"
                        class="toggle-input"
                        hidden
                      />
                      <span class="toggle-switch"></span>
                    </label>

                    <div
                      class="color-picker-subgroup"
                      :class="{ invisible: !obj.defaultRequiredBackground }"
                    >
                      <input
                        type="color"
                        id="color-background"
                        v-model="obj.defaultBackground"
                        class="color-input"
                      />
                    </div>
                  </div>
                  <div class="color-picker-group first-color-picker-group">
                    <label for="color-default" class="form-label">
                      Cor do texto
                    </label>
                    <input
                      type="color"
                      id="color-default"
                      v-model="obj.defaultColor"
                      class="color-input"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section class="style-section">
              <h4 class="section-title">Trechos com várias palavras</h4>
              <p class="section-help" >
                Para quando a nota marcar uma frase ou expressão.
              </p>
              
              <div class="color-section">
                <div 
                  class="color-preview" 
                  :style="[transparentBackgroundStyle, { 'padding': '1rem 0' }]"
                >
                  <span :style="multiWordPreviewStyle">
                    Trecho com várias palavras
                  </span>
                </div>
                
                <div class="color-pickers">
                  <div class="color-picker-group">
                    <label for="color-underline" class="form-label">
                      Cor do sublinhado
                    </label>
                    <input
                      type="color"
                      id="color-underline"
                      v-model="obj.defaultUnderline"
                      class="color-input"
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            
            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <span>
              {{ isSubmitting ? "Adicionando..." : "Adicionar Categoria" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[30000] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 38rem;
  height: auto;
  max-height: calc(100vh - 20px - 2rem);
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.explanation-text {
  font-size: 0.85rem;
  color: var(--text-light);
  margin-top: -0.25rem;
  line-height: 1.35;
}

/* =========================
   SECTIONS
========================= */
.style-section {
  border-top: 1px solid var(--border-color);
  padding-top: 0.9rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.25rem 0;
}

.section-help {
  font-size: 0.82rem;
  color: var(--text-light);
  margin: 0 0 0.75rem 0;
}

/* =========================
   COLOR LAYOUT
========================= */
.color-section {
  display: grid;
  grid-template-columns: 1.1fr 1.2fr;
  gap: 1rem;
  align-items: center;
}

.color-preview {
  width: 100%;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;
  font-weight: 600;
  border: 1px solid var(--border-color);

  transition: all 0.3s;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.color-pickers {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.first-color-picker-group {
  margin-left: 0;
  margin-top: 0.35rem;
  margin-bottom: 0.35rem;
}

/* =========================
   ✅ TOGGLE FIXADO AO LABEL
========================= */

/* Linha inteira */
.checkbox-and-picker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

/* Label + toggle juntos */
.toggle-control-group {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;

  width: auto;
  flex: 0 0 auto;
  cursor: pointer;
}

/* Evita quebra do texto */
.toggle-label-text {
  white-space: nowrap;
  font-weight: 500;
}

/* Toggle */
.toggle-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.toggle-switch {
  position: relative;
  display: block;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: var(--neutral-400);
  transition: background-color 0.2s ease;
  flex: 0 0 auto;
}

.toggle-switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

.toggle-input:checked + .toggle-switch {
  background-color: var(--success);
}

.toggle-input:checked + .toggle-switch::after {
  transform: translateX(16px);
}

/* Picker isolado à direita */
.color-picker-subgroup {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  transition: opacity 0.3s;
}

.color-picker-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: opacity 0.3s;
}

.color-picker-group.invisible,
.color-picker-subgroup.invisible {
  opacity: 0;
  pointer-events: none;
}

.color-input {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  width: 2.3rem;
  height: 2.3rem;

  background-color: transparent;
  border: 2px solid var(--border-color);
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
}

.color-input::-webkit-color-swatch,
.color-input::-moz-color-swatch {
  border-radius: 6px;
  border: none;
}

</style>
